import { Component, Input, SimpleChanges } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { ApplicationSchemeXActiveIngredientRate, ApplicationSchemeXApplication, ApplicationSchemeXApplicationWindow } from 'src/app/shared/models/application-scheme';
import { Precursor } from 'src/app/shared/models/echo/precursor';
import { Endpoint } from 'src/app/shared/models/endpoint';
import { Project } from 'src/app/shared/models/project';
import { Run } from 'src/app/shared/models/run/run';
import { QcIconComponent } from 'src/app/shared/renderers/qc-icon/qc-icon.component';
import { Constants } from 'src/app/shared/utils/constants';
import { Utils } from 'src/app/shared/utils/utils';
import { EEARunProjectQcColdef } from './eea-run-project-qc.coldef';
import { ProjectXCompoundXModel } from 'src/app/shared/models/project-x-compound-x-model';
import { EeaGapSurfacewaterPwcScenariosLogicService } from 'src/app/environmental-exposure-assessment/eea-gap/eea-gap-surfacewater-pwc/eea-gap-surfacewater-pwc-scenarios/eea-gap-surfacewater-pwc-scenarios.logic.service';
import { MitigationsXApplicationScheme } from 'src/app/shared/models/mitigation-x-application-scheme';
import { EEAGapLogicService } from 'src/app/environmental-exposure-assessment/eea-gap/eea-gap.logic.service';
import { isPwcV2Project } from 'src/app/shared/utils/pwc-version.utils';

@Component({
  selector: 'app-eea-run-project-qc',
  templateUrl: './eea-run-project-qc.component.html',
  styleUrls: ['./eea-run-project-qc.component.css']
})
export class EEARunProjectQCComponent {

  @Input() runs!: Run[];
  @Input() selectedProject?: Project;
  @Input() isPWC!: boolean;
  columnsToShow: string[] = []
  missingDataRows: any[] = []
  showGrid: Boolean = false;
  showWarningAlert: Boolean = false;
  warningAlertText: string = '';
  showAlert: Boolean = false;
  columnsDef: any;
  rowData: any;


  constructor(private qcColdef: EEARunProjectQcColdef,
    private scenariosPwcService: EeaGapSurfacewaterPwcScenariosLogicService,
    private gapLogicService: EEAGapLogicService
  ) { }

  async ngOnInit(): Promise<void> {
    if (this.isPWC && this.selectedProject) {
      const geography = isPwcV2Project(this.selectedProject) ? Constants.GEOGRAPHYS.BRAZIL : Constants.GEOGRAPHYS.USA;
      await this.scenariosPwcService.getCropsXRegion(geography);
    }
    await this.checkMissingDataByRow();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['runs'].currentValue) {
      this.showGrid = false;
      this.showWarningAlert = false;
    }
  }

  groupRowsByModel(): { [key: string]: Run[] } {
    let groupDataByModel: { [key: string]: Run[] } = {};
    this.runs.forEach(run => {
      const groupKey = run!.model!.name;
      if (!groupDataByModel[groupKey]) {
        groupDataByModel[groupKey] = [];
      }
      groupDataByModel[groupKey].push(run);
    });
    return groupDataByModel;
  }

  async checkMissingDataByRow() {
    let missingData: any[] = [];
    const groupedRows: { [key: string]: Run[] } = this.groupRowsByModel();
    if (Object.keys(groupedRows).length > 0) {
      Object.entries(groupedRows).forEach(([key, value]) => {
        var requiredColumns = this.validateRequiredColumnsByModel(key)
        if (requiredColumns) {
          requiredColumns = [...requiredColumns];
          const rowsImcomplete = this.isPWC
            ? this.checkMissingDataPWC(value, requiredColumns)
            : this.checkMissingData(value, requiredColumns);
          missingData.push(...rowsImcomplete)
        }
      });
      this.columnsToShow = [...new Set(missingData.flatMap(obj => obj.allMissingColumns))];
      this.missingDataRows = missingData;
      if (this.isPWC) {
        this.insertPWCDynamicColumnDefs();
      } else {
        this.insertDynamicColumnDefs();
      }
      this.showQcResult();
    } else {
      this.createWarningAlert();
    }
  }

  createWarningAlert(): void {
    if (this.selectedProject?.projectPk === -1) {
      this.warningAlertText = 'There is no a selected project.'
    }
    else if (this.selectedProject?.dataSets == null) {
      this.warningAlertText = 'There are no datasets for this project.'
    }
    else if (this.runs.length === 0) {
      this.warningAlertText = 'There are no runs to be process by the QC'
    }
    this.showWarningAlert = true;
  }

  checkMissingDataForMetabolites(pData: any): { [key: string]: { column: string, message: string }[] }[] {
    let missingDataByMetabolites: { [key: string]: { column: string, message: string }[] }[] = [];
    if (Constants.PWC_VERSIONS.includes(pData?.model?.name) && !pData?.dataSet?.daughterPk) {
      
      return [];
    }
    pData?.metabolites?.forEach((metabolite: Endpoint) => {
      let metaboliteColumns: string[] = Constants.METABOLITES_BY_MODEL[pData?.model?.name] ?? [];
      metaboliteColumns = this.includeColumnsByConditions(metabolite, metaboliteColumns, pData?.model?.name);
      let columnsByMetabolite: { column: string, message: string }[] = [];
      for (const column of metaboliteColumns) {
        let value = metabolite[column as keyof Endpoint];
        if (this.metaboliteOrderMissing(value, column)) {
          columnsByMetabolite.push({ column: column, message: 'MACRO GW mets ordering is missing ' });
        }
        else if (this.metaboliteOrderIsInvalid(pData, value, column, metabolite)) {
          columnsByMetabolite.push({ column: column, message: 'MACRO GW mets ordering is wrong, a precursor is not up in the ordering ' });
        }
        else if (column === Constants.FIELD_NAMES.HALFLIFE_IN_SOIL && [Constants.MODELS.PEARL, Constants.MODELS.SWASH].includes(pData?.model?.name) && !Utils.isEmptyValue(value) && value && (value as number) < Constants.MINIMIUM_HALF_LIFE_IN_SOIL_VALUE) {
          columnsByMetabolite.push({ column: column, message: `The value cannot be less than ${Constants.MINIMIUM_HALF_LIFE_IN_SOIL_VALUE} ` });

        }
        else if (Utils.isEmptyValue(value)) {
          columnsByMetabolite.push({ column: column, message: 'Missing Data' });
        }

      }
      let metaboliteFormationFractions = this.checkMissingDataForFractions(metabolite, pData.model.name);
      columnsByMetabolite = [...columnsByMetabolite, ...metaboliteFormationFractions];
      columnsByMetabolite.length > 0 && missingDataByMetabolites.push({ [metabolite.substanceName!]: columnsByMetabolite });
    });
    return missingDataByMetabolites;
  }

  metaboliteOrderMissing(value: any, column: string): boolean {
    return column == Constants.FIELD_NAMES.ORDER && value == 0;
  }

  metaboliteOrderIsInvalid(data: any, value: any, column: string, metabolite: Endpoint): boolean {
    if (column == Constants.FIELD_NAMES.ORDER) {
      if (value == 0) return false;
      for (const precursor of metabolite.precursors) {
        let metaboliteHasRightOrdering = precursor.moleculePk !== null || data.metabolites.some(
          (m: Endpoint) =>
            m.substanceName === precursor.substanceName &&
            m.order && metabolite.order && m.order < metabolite.order
        ) || this.isParentMetabolite(data, precursor);

        if (!metaboliteHasRightOrdering) {
          return true
        }
      }
    }
    return false;
  }

  isParentMetabolite(data: any, precursor: Precursor): boolean {
    return data?.substanceName === precursor.substanceName;
  }

  checkMissingDataForFractions(metabolite: Endpoint, pModelName: string): { column: string, message: string }[] {
    let missingColumns: { column: string, message: string }[] = [];
    if (!Constants.MODELS_THAT_REQUIRE_FORMATION_FRACTION.find(x => x == pModelName)) {
      return [];
    }
    missingColumns = [...this.validateExistencePrecursorsInFormationFractions(metabolite, pModelName)];
    this.calculateMissingDataForPrecursors(metabolite, missingColumns, pModelName);
    return [...new Set(missingColumns)];
  }

  // TODO: This code is commented out, to prevent QC from detecting it, in future stories it will be addressed.
  private calculateMissingDataForPrecursors(metabolite: Endpoint, missingColumns: { column: string; message: string; }[], pModelName: string) {
    // let sumFormationFraction = 0;
    metabolite?.precursors?.forEach((precursor: Precursor) => {
      for (const column of Constants.COLUMS_BY_FRACTIONS) {
        let value = precursor[column as keyof Precursor];
        if (Utils.isEmptyValue(value)) {
          missingColumns.push({ column: precursor.precursorType, message: 'Missing Data' });
          continue;
        }
        // if (pModelName === Constants.MODELS.PELMO && column === Constants.FIELD_NAMES.FORMATION_FRACTION) {
        //   sumFormationFraction += Number(precursor[column as keyof Precursor]);
        // }
      }
    });
    // if (pModelName === Constants.MODELS.PELMO && sumFormationFraction > 1) {
    //   missingColumns.push({ column: Constants.FIELD_NAMES.FRACTION_IN_SOIL, message: 'The sum of the fraction transformed in soil must be equal or less than 1' });
    // }
  }

  validateExistencePrecursorsInFormationFractions(metabolite: Endpoint, pModelName: string): { column: string, message: string }[] {
    let fractions = metabolite?.precursors?.flatMap((precursor: Precursor) => precursor.precursorType) || [];
    return Constants.REQUIRED_FRACTIONS_BY_METABOLITES_BY_MODEL[pModelName]
      .filter((x: string) => !fractions.includes(x))
      .map((missingFraction: string) => ({ column: missingFraction, message: 'Missing Data' }));
  }

  validateRequiredColumnsByModel(model: string): any {
    let columns = Constants.REQUIRED_COLUMNS_BY_MODEL[model];
    if (columns) {
      columns = this.includeColumnsByModel(model, columns);
    }
    return columns;
  }

  validateCropEvent(run: Run, pColumns: string[]): any {
    let columns = pColumns;
    if (Constants.CROP_EVENT_EXCEPTION_MODELS.includes(run.model!.name)) {
      let dateType = run?.applicationSchemeData?.dateType;
      if (!Utils.isEmptyValue(dateType)) {
        columns = dateType === Constants.DATE_TYPE_VALUES.ABSOLUTE ?
          columns.filter((field: any) => !([Constants.FIELD_NAMES.APP_SCHEME_CROP_EVENT, Constants.FIELD_NAMES.APP_SCHEME_DAYS_SINCE].includes(field))) :
          columns;
      } else {
        columns = columns.filter((field: any) => !([Constants.FIELD_NAMES.APP_SCHEME_CROP_EVENT, Constants.FIELD_NAMES.APP_SCHEME_DAYS_SINCE].includes(field)));
      }
    }
    return columns;
  }

  validateVariableRates(run: Run, pColumns: string[]): any {
    let columns = pColumns;
    if (Constants.DATE_TYPE_EXCEPTION_MODELS.includes(run.model!.name)) {
      if (run?.applicationSchemeData?.hasVariableRates) {
        columns = columns.filter((field: any) => !(Constants.MAIN_APPLICATIONS_FIELDS.includes(field)))
      }
    }
    return columns;
  }

  validateCropPk(run: Run, pColumns: string[]): any {
    let columns = [...pColumns];
    if (Utils.isEmptyValue(run?.applicationSchemeData?.coreApplicationSchemePk)) {
      columns = columns.filter((field: string) => field !== Constants.FIELD_NAMES.GLOBAL_CROP);
    }
    return columns;
  }

  validateApplicationNumberPWC(run: Run, pColumns: string[]) {
    let columns = [...pColumns];
    if (run.applicationSchemeDataList?.filter((scheme: any) => Utils.isEmptyValue(scheme?.applicationSchemeXApplication))) {
      columns = columns.filter((field: string) => field !== Constants.PWC_FIELD_NAMES.NUMBER_OF_APPLICATIONS);
    }

    return columns;
  }

  includeSwanColumns(model: string, pColumns: string[]): string[] {
    let columns = [...pColumns];
    if (model === Constants.MODELS.SWASH) {
      let runSwanModel = this.selectedProject!.projectXCompoundXModel?.some((x: ProjectXCompoundXModel) => x.ModelName === Constants.MODELS.SWAN);
      if (runSwanModel) {
        columns = [...columns, Constants.FIELD_NAMES.APP_SCHEME_CROP_EVA];
      }
    }
    return columns;
  }

  includeColumnsByModel(model: string, pColumns: string[]): string[] {
    let columns = [...pColumns];
    switch (model) {
      case Constants.MODELS.PWC:
      case Constants.MODELS.PWC2:
        if (this.selectedProject!.projectXCompoundXModel?.some((x: ProjectXCompoundXModel) => Constants.PWC_VERSIONS.includes(x.ModelName!))) {
          columns = [...columns];
        }
        break;
    }
    return columns;
  }

  excludeColumnsByConditions(run: Run, pColumns: string[]): any {
    let columns = [...pColumns];
    columns = this.validateVariableRates(run, columns);
    columns = this.validateCropEvent(run, columns);
    columns = this.validateCropPk(run, columns);
    return columns;
  }

  excludeColumnsByConditionsForPWC(run: Run, pColumns: string[]): any {
    let columns = [...pColumns];
    columns = this.validateCropEvent(run, columns);
    columns = this.validateApplicationNumberPWC(run, columns);
    return columns;
  }

  includeColumnsByConditions(run: Run, pColumns: string[], modelName: string): any {
    let columns = [...pColumns];
    columns = this.includeColumnsByKineticModel(run, columns, modelName);
    columns = this.includeApplicationInterval(run, columns, modelName);
    return columns;
  }

  includeColumnsByKineticModel(run: Run, pColumns: string[], modelName: string): any {
    if (Constants.SOIL_MODELS.includes(modelName) && run.kineticModel != Constants.KINETIC_MODELS.SFO) {
      return [...pColumns, ...(Constants.REQUIRED_COLUMNS_BY_KINETIC_MODEL[run.kineticModel as string] ?? [])];
    }
    return pColumns;
  }

  includeApplicationInterval(run: Run, requiredColumns: string[], modelName: string): string[] {
    if (Constants.DATE_TYPE_EXCEPTION_MODELS.includes(modelName)) {
      let dateType = run?.applicationSchemeData?.dateType;
      if (!Utils.isEmptyValue(dateType)) {
        return dateType === Constants.DATE_TYPE_VALUES.RELATIVE ? [...requiredColumns, Constants.FIELD_NAMES.APP_SCHEME_APPLICATION_INTERVAL] : requiredColumns;
      }
    }
    return requiredColumns;
  }

  checkMissingData(runs: Run[], requiredColumns: string[]) {
    let missingDataByRunId: any[] = [];
    runs.forEach((run: Run) => {
      let missingColumns = [];
      let columnsToValidate = [...requiredColumns];
      columnsToValidate = this.excludeColumnsByConditions(run, columnsToValidate);
      columnsToValidate = this.includeColumnsByConditions(run, columnsToValidate, run.model!.name);
      columnsToValidate = this.includeSwanColumns(run.model!.name, columnsToValidate);
      for (const column of columnsToValidate) {
        const properties = column.split('.');
        let nestedData = run;
        for (const property of properties) {
          if (nestedData && nestedData.hasOwnProperty(property)) {
            nestedData = (nestedData as any)[property];
          } else {
            missingColumns.push(column);
            break;
          }
        }
        if (Utils.isEmptyValue(nestedData)) {
          missingColumns.push(column);
        }
      }
      let metabolites = this.checkMissingDataForMetabolites(run);
      let rates = this.checkMissingDataForRates(run);
      let applicationsxApplications = this.checkMissingDataForApplicationsXApplications(run, rates);
      let applicationScheme = this.checkMissingDataForApplicationScheme(run);
      let mitigations = this.checkMissingDataForMitigations(run);
      let depositionCropInterception = this.checkMissingCropInterceptionAndDeposition(run);
      let depositionRates = this.checkMissingDepositionRates(run);
      let allMissingColumns = [...new Set([...missingColumns, ...applicationsxApplications, ...mitigations, ...depositionCropInterception, ...depositionRates, ...applicationScheme.map(item => item.column), ...this.getAllColumnValues(metabolites), ...Object.values(rates).flat()])]
      if (allMissingColumns.length > 0) {
        missingDataByRunId.push({
          run,
          allMissingColumns,
          missingParentColumns: missingColumns,
          missingColumnsByMetabolites: metabolites,
          missingColumnsByApplications: rates,
          missingColumnsMessages: [...new Set([...applicationScheme])]
        })
      }
    });
    return missingDataByRunId;
  }

  checkMissingDataForMitigations(run: Run): string[] {
    let columnsWithErrors: string[] = [];
    const modelName = run.model?.name;
    const applicationSchemeData = run.applicationSchemeData?.applicationSchemeXApplicationWindow;
    let runSwanModel = this.selectedProject!.projectXCompoundXModel?.some((x: ProjectXCompoundXModel) => x.ModelName === Constants.MODELS.SWAN);

    if (!modelName || modelName !== Constants.MODELS.SWAN || !applicationSchemeData || applicationSchemeData.length === 0 || !runSwanModel)
      return columnsWithErrors;

    const scenariosForMitigations = this.getScenariosForMitigations(applicationSchemeData);
    if (scenariosForMitigations.length > 0) {
      const mitigationsXApplicationScheme = run.applicationSchemeData?.mitigationsXApplicationScheme;
      if (mitigationsXApplicationScheme.length > 0) {
        mitigationsXApplicationScheme.map((row: MitigationsXApplicationScheme) => {
          const columnsToValidate = this.getColumnsForMitigation(scenariosForMitigations, row);
          columnsToValidate.map(field => {
            if (Utils.isEmptyValue(row[field as keyof MitigationsXApplicationScheme])) {
              columnsWithErrors.push(field);
            }
          });
        });
      } else {
        columnsWithErrors.push(Constants.FIELD_NAMES.STEP_4_MITIGATION)
      }
    } else {
      columnsWithErrors.push(...Constants.MITIGATION_SCENARIOS)
    }
    return [...new Set(columnsWithErrors)];
  }

  getColumnsForMitigation(scenarios: string[], row: MitigationsXApplicationScheme): string[] {
    const columns = [Constants.FIELD_NAMES.MITIGATION_NAME];

    if (scenarios.includes(Constants.FIELD_NAMES.RUN_OFF)) {
      columns.push(...Constants.REQUIRED_COLUMNS_FOR_RUN_OFF);
    }
    if (scenarios.includes(Constants.FIELD_NAMES.SPRAY_DRIFT)) {
      columns.push(...Constants.REQUIRED_COLUMNS_FOR_SPRAY_DRIFT);
      if (row.sprayDriftOption === Constants.MITIGATION_SPRAY_DRIFT_OPTION_SDO_02) {
        columns.push(...Constants.REQUIRED_COLUMNS_FOR_SPRAY_DRIFT_BUFFER);
      }
      if (row.sprayDriftOption === Constants.MITIGATION_SPRAY_DRIFT_OPTION_SDO_03) {
        columns.push(...Constants.REQUIRED_COLUMNS_FOR_SPRAY_DRIFT_MASS);
      }
    }
    if (scenarios.includes(Constants.FIELD_NAMES.DEPOSITION)) {
      columns.push(...Constants.REQUIRED_COLUMNS_FOR_DEPOSITION);
    }
    return columns;
  }

  getScenariosForMitigations(dataArray: ApplicationSchemeXApplicationWindow[]): string[] {
    const scenarios: string[] = [];
    Constants.MITIGATION_SCENARIOS.forEach((scenario) => {
      if (dataArray.some(item => item[scenario as keyof ApplicationSchemeXApplicationWindow])) {
        scenarios.push(scenario);
      }
    });

    return scenarios;
  }

  checkMissingCropInterceptionAndDeposition(run: Run): string[] {
    let columnsWithErrors: string[] = [];
    if (run.model?.name !== Constants.MODELS.SWAN) {
      return columnsWithErrors;
    }

    const hasDepositionWindow =
      run.applicationSchemeData?.applicationSchemeXApplicationWindow?.some(
        (x: ApplicationSchemeXApplicationWindow) => x.deposition === true
      );

    if (!hasDepositionWindow) {
      return columnsWithErrors;
    }
    if (!run.applicationSchemeData?.useSwanCropInterception) {
      return columnsWithErrors;
    }
    if (Utils.isEmptyValue(run.applicationSchemeData?.swanCropInterception) || run.applicationSchemeData.swanCropInterception <= 0) {
      columnsWithErrors.push(Constants.FIELD_NAMES.SWAN_CROP_INTERCEPTION);
    }
    return [...new Set(columnsWithErrors)];
  }

  checkMissingDepositionRates(run: Run): string[] {
    let columnsWithErrors: string[] = [];
    if (run.model?.name !== Constants.MODELS.SWAN) {
      return columnsWithErrors;
    }

    const depositionWindows =
      run.applicationSchemeData?.applicationSchemeXApplicationWindow?.filter(
        (x: ApplicationSchemeXApplicationWindow) => x.deposition === true
      );

    if (!depositionWindows || depositionWindows.length === 0) {
      return columnsWithErrors;
    }
    const activeIngredientRates =
      run.applicationSchemeData?.applicationSchemeXActiveIngredientRate;
    if (!activeIngredientRates || activeIngredientRates.length === 0) {
      columnsWithErrors.push(...Constants.REQUIRED_FIELDS_BY_DEPOSITION_RATE);
    } else {
      activeIngredientRates.forEach(
        (rate: ApplicationSchemeXActiveIngredientRate) => {
          if (depositionWindows) {
            const columnsToValidate = Constants.REQUIRED_FIELDS_BY_DEPOSITION_RATE;
            columnsToValidate.map((field: string) => {
              if (Utils.isEmptyValue(rate[field as keyof ApplicationSchemeXActiveIngredientRate])) {
                columnsWithErrors.push(field);
              }
            });
          }
        },
      );
    }
    return [...new Set(columnsWithErrors)];
  }

  checkMissingDataPWC(runs: Run[], requiredColumns: string[]) {
    let missingDataByRunId: any[] = [];
    runs.forEach((run: Run) => {
      let missingColumns = [];
      let columnsToValidate = [...requiredColumns];
      columnsToValidate = this.excludeColumnsByConditionsForPWC(run, columnsToValidate);
      for (const column of columnsToValidate) {
        const properties = column.split('.');
        let nestedData = run;
        for (const property of properties) {
          if (nestedData && nestedData.hasOwnProperty(property)) {
            nestedData = (nestedData as any)[property];
          } else {
            missingColumns.push(column);
            break;
          }
        }
        if (Utils.isEmptyValue(nestedData)) {
          missingColumns.push(column);
        }
      }
      let rates = this.checkMissingDataForRatesForPWC(run);
      let metabolites = this.checkMissingDataForMetabolites(run);
      let applicationScheme = this.checkMissingDataForApplicationSchemeForPWC(run);
      let applicationsxApplications = this.checkMissingDataForApplicationsXApplicationsForPWC(run, rates);
      let allMissingColumns = [...new Set([...missingColumns, ...applicationsxApplications, ...applicationScheme.map(item => item.column), ...this.getAllColumnValues(metabolites), ...Object.values(rates).flat()])]
      if (allMissingColumns.length > 0) {
        missingDataByRunId.push({
          run,
          allMissingColumns,
          missingParentColumns: missingColumns,
          missingColumnsByMetabolites: metabolites,
          missingColumnsByApplications: rates,
          missingColumnsMessages: [...new Set([...applicationScheme])]
        })
      }
    });
    return missingDataByRunId;
  }

  checkMissingDataForApplicationScheme(run: Run): { column: string, message: string }[] {
    let columns = [];
    if (run.model?.name === Constants.MODELS.SWASH && run.applicationSchemeData?.numberOfApplications! > 5) {
      columns.push({ column: Constants.FIELD_NAMES.NUMBER_OF_APPLICATIONS, message: 'Number of applications exceeds 5 Applications.' });
    }
    if ((run.model?.name === Constants.MODELS.PEARL && (run.applicationSchemeData?.repeatInterval! < 1 || run.applicationSchemeData?.repeatInterval! > 3)) ||
      (run.model?.name === Constants.MODELS.PELMO && run.applicationSchemeData?.repeatInterval! < 1)) {
      columns.push({ column: Constants.FIELD_NAMES.APP_SCHEME_REPEAT_INTERVAL, message: run.model?.name === Constants.MODELS.PELMO ? 'The value must be greater than 0.' : 'The value must be in the range of 1 to 3.' });
    }
    if (run.model?.name === Constants.MODELS.PELMO && !Utils.isEmptyValue(run.applicationSchemeData?.soilDepth) && Number(run.applicationSchemeData?.soilDepth) > Constants.PELMO_MAX_SOIL_DEPTH_M) {
      columns.push({ column: Constants.FIELD_NAMES.APP_SCHEME_SOIL_DEPTH, message: `The value cannot be greater than ${Constants.PELMO_MAX_SOIL_DEPTH_M} m.` });
    }
    if ([Constants.MODELS.PEARL, Constants.MODELS.SWASH].includes(run.model?.name!) && !Utils.isEmptyValue(run.halfLifeInSoil) && run.halfLifeInSoil && run.halfLifeInSoil < Constants.MINIMIUM_HALF_LIFE_IN_SOIL_VALUE) {
      columns.push({ column: Constants.FIELD_NAMES.HALFLIFE_IN_SOIL, message: `The value cannot be less than ${Constants.MINIMIUM_HALF_LIFE_IN_SOIL_VALUE}.` });
    }
    if (run.model?.name === Constants.MODELS.SWASH && run.applicationSchemeData?.useSwashCropInterception && Utils.isEmptyValue(run.applicationSchemeData?.swashCropInterception)) {
      columns.push({ column: Constants.FIELD_NAMES.SWASH_CROP_INTERCEPTION, message: 'Missing data.' });
    }
    if ([Constants.MODELS.SWASH, Constants.MODELS.SWAN].includes(run.model?.name!)) {
      const pathLengthValidation = this.validatePathLength(run);
      if (pathLengthValidation) {
        columns.push(pathLengthValidation);
      }
    }
    return columns;
  }

  private validatePathLength(run: Run): { column: string, message: string } | null {
    const MAX_PATH_LENGTH = 250;
    const BASE_STATIC_LENGTH = 64;
    const ESTIMATED_RUN_ID_LENGTH = 10;

    const datasetName = (run.dataSet?.description || '').replace(/\s+/g, '_').replace(/,/g, '');
    const applicationSchemeName = (run.applicationSchemeData?.name || '').replace(/\s+/g, '_').replace(/,/g, '');

    if (run.model?.name === Constants.MODELS.SWAN) {
      const mitigations = run.applicationSchemeData?.mitigationsXApplicationScheme || [];
      
      for (const mitigation of mitigations) {
        const mitigationName = (mitigation.mitigationName || '').replace(/\s+/g, '_').replace(/,/g, '');
        const STEP4_SUFFIX_LENGTH = 7;
        
        const totalPathLength = BASE_STATIC_LENGTH + 
          ESTIMATED_RUN_ID_LENGTH +
          (applicationSchemeName.length * 2) +
          (mitigationName.length * 2) +
          (datasetName.length * 2) +
          STEP4_SUFFIX_LENGTH;

        if (totalPathLength > MAX_PATH_LENGTH) {
          const availableLength = MAX_PATH_LENGTH - BASE_STATIC_LENGTH - ESTIMATED_RUN_ID_LENGTH;
          const permittedCombinedLength = Math.floor(availableLength / 2) - mitigationName.length;
          return {
            column: Constants.FIELD_NAMES.PATH_LENGTH_EXCEEDED,
            message: `The length of the Dataset, Application Scheme, and Mitigation ('${mitigationName}') names together exceed the permitted limit of ${permittedCombinedLength} characters available. Please shorten any or all of them.`
          };
        }
      }
    } else {
      const totalPathLength = BASE_STATIC_LENGTH + 
        ESTIMATED_RUN_ID_LENGTH +
        (applicationSchemeName.length * 2) +
        (datasetName.length * 2);

      if (totalPathLength > MAX_PATH_LENGTH) {
        const availableLength = MAX_PATH_LENGTH - BASE_STATIC_LENGTH - ESTIMATED_RUN_ID_LENGTH;
        const permittedCombinedLength = Math.floor(availableLength / 2);
        return {
          column: Constants.FIELD_NAMES.PATH_LENGTH_EXCEEDED,
          message: `The length of the Dataset and Application Scheme names together exceed the permitted limit of ${permittedCombinedLength} characters. Please shorten any or both of them.`
        };
      }
    }

    return null;
  }

  checkMissingDataForApplicationSchemeForPWC(run: Run): { column: string, message: string }[] {
    let columns = [];
    if (run.applicationSchemeDataList?.filter((scheme: any) => Utils.isEmptyValue(scheme?.numberOfApplications)).length > 0) {
      columns.push({ column: Constants.FIELD_NAMES.NUMBER_OF_APPLICATIONS, message: 'Missing applications' });
    }
    if (run.applicationSchemeDataList?.filter((scheme: any) => Utils.isEmptyValue(scheme?.dateType)).length > 0) {
      columns.push({ column: Constants.PWC_FIELD_NAMES.DATE_TYPE, message: 'Missing Date Type' });
    }
    if (run.applicationSchemeDataList?.filter((scheme: any) => !scheme?.cropXRegionPks.length).length > 0) {
      columns.push({ column: Constants.PWC_FIELD_NAMES.SCENARIOS, message: 'Missing Scenarios' });
    }

    run.applicationSchemeDataList?.forEach((scheme: any) => {
      scheme.crops = scheme?.cropXRegionPks?.map((cropXRegionPk: number) => {
        const crop = this.scenariosPwcService.cropXRegion.find(cropXRegion => cropXRegion.crop_x_region_pk === cropXRegionPk)?.crop;
        return crop;
      });
    });



    run.applicationSchemeDataList?.forEach((scheme: any) => {
      let onlyTurfOrNursery = false;
      let hasTurforNursery = false;

      scheme?.crops?.forEach((crop: string) => {
        if (crop && ['turf', 'nursery'].includes(crop.toLowerCase()))
          hasTurforNursery = true;
      });

      const number = scheme?.crops.filter((crop: string) => crop && !['turf', 'nursery'].includes(crop.toLowerCase()));

      if (number.length === 0) {
        onlyTurfOrNursery = true;
      }

      const emptyKocRange = Utils.isEmptyValue(scheme?.kocRange);
      const isPwcV2 = !!this.selectedProject && isPwcV2Project(this.selectedProject);

      if (!isPwcV2 && ((emptyKocRange && hasTurforNursery && !onlyTurfOrNursery) || (emptyKocRange && !hasTurforNursery)))
        columns.push({ column: Constants.PWC_FIELD_NAMES.KOC_RANGE, message: 'Missing Koc Range' });

    });

    return columns;
  }

  getAllColumnValues(data: { [key: string]: { column: string, message: string }[] }[]): string[] {
    const columnValues: string[] = [];

    for (const obj of data) {
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          for (const item of obj[key]) {
            columnValues.push(item.column);
          }
        }
      }
    }

    return columnValues;
  }

  checkMissingDataForRates(run: Run): { [key: string]: string[] } {
    if (!this.validateRatesFromRun(run)) return {};
    let missingColumnsByMolecule: { [key: string]: string[] } = {};
    run.applicationSchemeData?.applicationSchemeXActiveIngredientRate?.forEach((item: ApplicationSchemeXActiveIngredientRate) => {
      let missingColumns: string[] = [];
      Constants.REQUIRED_FIELDS_BY_RATES.forEach((field: string) => {
        if (Utils.isEmptyValue(item[field as keyof ApplicationSchemeXActiveIngredientRate])) {
          missingColumns.push(field)
        }
      });
      if (!missingColumnsByMolecule[item.substanceName!]) {
        missingColumnsByMolecule[item.substanceName!] = [...missingColumns]
      } else {
        missingColumnsByMolecule[item.substanceName!] = [...missingColumnsByMolecule[item.substanceName!], ...missingColumns]
      };
    });
    return missingColumnsByMolecule;
  }

  checkMissingDataForRatesForPWC(run: Run): { [key: string]: string[] } {
    if (!this.validateRatesFromRunForPWC(run)) return {};
    let missingColumnsByMolecule: { [key: string]: string[] } = {};
    run.applicationSchemeDataList?.forEach((scheme: any) => {
      if (!scheme?.applicationSchemeXActiveIngredientRate?.length) {
        missingColumnsByMolecule[scheme?.name ?? 'scheme'] = [Constants.FIELD_NAMES.RATE];
        return;
      }
      scheme?.applicationSchemeXActiveIngredientRate?.forEach((item: ApplicationSchemeXActiveIngredientRate) => {
        let missingColumns: string[] = [];
        Constants.REQUIRED_FIELDS_BY_RATES.forEach((field: string) => {
          if (Utils.isEmptyValue(item[field as keyof ApplicationSchemeXActiveIngredientRate])) {
            missingColumns.push(field)
          }
        });
        if (!missingColumnsByMolecule[item.substanceName!]) {
          missingColumnsByMolecule[item.substanceName!] = [...missingColumns]
        } else {
          missingColumnsByMolecule[item.substanceName!] = [...missingColumnsByMolecule[item.substanceName!], ...missingColumns]
        };
      });
    });
    return missingColumnsByMolecule;
  }

  validateRatesInApplication(application: ApplicationSchemeXApplication, rates: { [key: string]: string[] }): void {
    application.applicationSchemeXActiveIngredientRate?.forEach((item: ApplicationSchemeXActiveIngredientRate) => {
      let missingColumns: string[] = [];
      Constants.REQUIRED_FIELDS_BY_RATES.forEach((field: string) => {
        if (Utils.isEmptyValue(item[field as keyof ApplicationSchemeXActiveIngredientRate])) {
          missingColumns.push(field)
        }
      });
      if (!rates[item.substanceName!]) {
        rates[item.substanceName!] = [...missingColumns]
      } else {
        rates[item.substanceName!] = [...rates[item.substanceName!], ...missingColumns]
      };
    });
  }

  validateRatesInApplicationForPWC(application: ApplicationSchemeXApplication, rates: { [key: string]: string[] }): void {
    application.applicationSchemeXActiveIngredientRate?.forEach((item: ApplicationSchemeXActiveIngredientRate) => {
      let missingColumns: string[] = [];
      Constants.REQUIRED_FIELDS_BY_RATES.forEach((field: string) => {
        if (Utils.isEmptyValue(item[field as keyof ApplicationSchemeXActiveIngredientRate])) {
          missingColumns.push(field)
        }
      });
      if (!rates[item.substanceName!]) {
        rates[item.substanceName!] = [...missingColumns]
      } else {
        rates[item.substanceName!] = [...rates[item.substanceName!], ...missingColumns]
      };
    });
  }

  validateRatesFromRun(run: Run): boolean {
    if (run.applicationSchemeData?.hasVariableRates) {
      if ([Constants.MODELS.STEP_1_2, Constants.MODELS.UK, Constants.MODELS.EU_SOIL, Constants.MODELS.GERMAN_SOIL, Constants.MODELS.NEU_SOIL].includes(run.model!.name)) {
        return true;
      }
      if (Constants.MODELS_FOR_VARIABLE_RATES.includes(run.model!.name)) {
        return false;
      }
    }
    return true;
  }

  validateRatesFromRunForPWC(run: Run): boolean {
    if (run.applicationSchemeDataList?.filter((x: any) => x.hasVariableRates).length > 0) {
      if ([Constants.MODELS.STEP_1_2, Constants.MODELS.UK, Constants.MODELS.EU_SOIL, Constants.MODELS.GERMAN_SOIL, Constants.MODELS.NEU_SOIL].includes(run.model!.name)) {
        return true;
      }
      if (Constants.MODELS_FOR_VARIABLE_RATES.includes(run.model!.name)) {
        return false;
      }
    }
    return true;
  }

  GetRateColumnsToBeValidated(run: Run): string[] {
    if (Constants.DATE_TYPE_EXCEPTION_MODELS.includes(run.model!.name)) {
      let dateType = run?.applicationSchemeData?.dateType;
      if (!Utils.isEmptyValue(dateType)) {
        return dateType === Constants.DATE_TYPE_VALUES.RELATIVE ? Constants.REQUIRED_FIELDS_BY_RATES : Constants.REQUIRED_FIELDS_BY_RATES.filter((field: string) => field !== Constants.FIELD_NAMES.APPLICATION_INTERVAL);
      }
    }
    return Constants.REQUIRED_FIELDS_BY_RATES;
  }

  checkMissingDataForApplicationsXApplications(run: Run, rates: { [key: string]: string[] }): string[] {
    let missingColumnsByApplications: string[] = [];
    if (run.applicationSchemeData?.hasVariableRates && Constants.MODELS_FOR_VARIABLE_RATES.includes(run.model!.name)) {
      let columnsToValidate = this.getApplicationColumnsToBeValidated(run);
      run.applicationSchemeData?.applicationSchemeXApplication?.forEach((application: ApplicationSchemeXApplication) => {
        columnsToValidate.forEach((field: string) => {
          if (Utils.isEmptyValue(application[field as keyof ApplicationSchemeXApplication])) {
            missingColumnsByApplications.push(field)
          }
        });
        this.validateRatesInApplication(application, rates);
      });
    }
    return missingColumnsByApplications;
  }

  checkMissingDataForApplicationsXApplicationsForPWC(run: Run, rates: { [key: string]: string[] }): string[] {
    let missingColumnsByApplications: string[] = [];
    const isPwcV2 = !!this.selectedProject && isPwcV2Project(this.selectedProject);
    run.applicationSchemeDataList?.forEach((scheme: any) => {
      scheme.applicationSchemeXApplication?.forEach((application: ApplicationSchemeXApplication) => {
        if (Utils.isEmptyValue(application.application_number)) {
          missingColumnsByApplications.push(Constants.PWC_FIELD_NAMES.NUMBER_OF_APPLICATIONS);
        }
        if (!isPwcV2 && Utils.isEmptyValue((application as any).drift_type)) {
          missingColumnsByApplications.push(Constants.PWC_FIELD_NAMES.DRIFT_TYPE);
        }
      });
      if (scheme?.hasVariableRates && Constants.MODELS_FOR_VARIABLE_RATES.includes(run.model!.name)) {
        let columnsToValidate = this.getApplicationColumnsToBeValidated(run);
        scheme.applicationSchemeXApplication?.forEach((application: ApplicationSchemeXApplication) => {
          columnsToValidate.forEach((field: string) => {
            if (Utils.isEmptyValue(application[field as keyof ApplicationSchemeXApplication])) {
              missingColumnsByApplications.push(field);
            }
          });
          this.validateRatesInApplicationForPWC(application, rates);
        });
      }
    });
    return [...new Set(missingColumnsByApplications)];
  }

  getApplicationColumnsToBeValidated(run: Run): string[] {
    if (Constants.DATE_TYPE_EXCEPTION_MODELS.includes(run.model!.name)) {
      let dateType = run?.applicationSchemeData?.dateType;
      let appColumns = run.model!.name === Constants.MODELS.MACRO_GW || run.model!.name === Constants.MODELS.PEARL || run.model!.name === Constants.MODELS.PELMO ? Constants.REQUIRED_APPLICATIONS_FIELDS.filter((field: string) => field !== Constants.EEA_APPLICATIONS_FIELDS.DAYS_SINCE) : Constants.REQUIRED_APPLICATIONS_FIELDS;
      if (!Utils.isEmptyValue(dateType)) {
        return dateType === Constants.DATE_TYPE_VALUES.RELATIVE ? appColumns : appColumns.filter((field: string) => field !== Constants.EEA_APPLICATIONS_FIELDS.DAYS_SINCE);
      }
    }
    return [];
  }

  validateAppSchemeRootFields(run: Run): string[] {
    let rootFields: string[] = [];

    if (Utils.isEmptyValue(run.applicationSchemeData?.numberOfApplications)) {
      rootFields.push(Constants.FIELD_NAMES.NUMBER_OF_APPLICATIONS);
    }

    return rootFields;
  }

  convertFieldToHeaderName(field: string) {
    return Constants.HEADER_NAMES_BY_FIELD[field]
  }

  insertDynamicColumnDefs() {
    this.columnsDef = [...this.qcColdef.getQcColumnsDefinition(), ...this.createDynamicColDef()]
  }

  insertPWCDynamicColumnDefs() {
    this.columnsDef = [...this.qcColdef.getPWCQcColumnsDefinition(), ...this.createDynamicColDef()]
  }

  createDynamicColDef(): any[] {
    let columnsDef: ColDef[] = [];
    this.columnsToShow?.forEach((column: string) => {
      columnsDef.push(
        {
          headerName: this.convertFieldToHeaderName(column),
          field: column,
          type: 'default',
          filter: 'agSetColumnFilter',
          width: 100,
          maxWidth: 120,
          hide: false,
          editable: false,
          wrapText: true,
          wrapHeaderText: true,
          cellRenderer: QcIconComponent,
          cellRendererParams: (params: any) => {
            return {
              instance: this,
            };
          },
        }
      );
    });
    return columnsDef;
  }

  showQcResult() {
    this.missingDataRows.length > 0 ? this.showGrid = true : this.showAlert = true;
  }
}


